import { useMutation } from 'react-query';

import { DEFAULT_STRIPE_METHOD, PAYMENTS_BACKEND_URL, PAYMENTS_ROOT_URL } from '@/config';
import axios from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { getMyRateURL } from '@/utils/helpers';

import { PaymentProps } from '..';

export type PaymentIntent = {
  id: string;
  clientSecret: string;
  amount: string | number;
  currency: string;
  sessionUrl: string | null;
};

export const createPayment = ({
  prices,
  preview = false,
  promotionCode,
  onErrorCallback,
}: PaymentProps): Promise<PaymentIntent> => {
  const endPoint = PAYMENTS_BACKEND_URL + 'payment';
  const priceId: string = prices[0];

  const postData = {
    prices: prices,
    preview,
    promotionCode,
    method: DEFAULT_STRIPE_METHOD,
    successUrl: getSuccessUrl(priceId),
    cancelUrl: getMyRateURL(),
  };

  return axios
    .post(endPoint, postData)
    .then((response: any) => {
      return response;
    })
    .catch((error) => {
      onErrorCallback && onErrorCallback(error.response.status);
      if (error.response.status === 409) {
        return;
      }
      if (error.response.status === 503) {
        return;
      }
      if (error.response.status === 500) {
        return;
      }
      throw Error(`Critical error fetching data in ${endPoint}: ${error}`);
    });
};

type UseCreatePaymentOptions = {
  config?: MutationConfig<typeof createPayment>;
};

export const useCreatePayment = ({ config }: UseCreatePaymentOptions = {}) => {
  return useMutation({
    onMutate: async (creatingNotification: any) => {
      await queryClient.cancelQueries(['payment', creatingNotification]);

      const previousDetails = queryClient.getQueryData<PaymentProps>([
        'payment',
        creatingNotification,
      ]);

      queryClient.setQueryData(['payment', creatingNotification], {
        ...previousDetails,
        ...creatingNotification.data,
      });

      return { previousDetails: previousDetails };
    },
    onError: (_, __, context: any) => {
      if (context?.previousDetails) {
        queryClient.setQueryData(['payment', context.previousDetails], context.previousDetails);
      }
    },
    onSuccess: (data) => {
      queryClient.refetchQueries(['payment', data]);
    },
    ...config,
    mutationFn: createPayment,
  });
};

const getSuccessUrl = (suffix = ''): string => `${PAYMENTS_ROOT_URL}account/purchase/${suffix}`;
